import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  fetchImpersonationBanner,
  stopImpersonation,
  useAuth,
  type ImpersonationBanner as Banner,
} from '@saas-kit/lib';

export function ImpersonationBanner(): JSX.Element | null {
  const { t } = useTranslation('common');
  const active = useAuth((s) => s.impersonation.active);
  const clearSession = useAuth((s) => s.clearSession);
  const navigate = useNavigate();
  const [banner, setBanner] = useState<Banner | null>(null);
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    if (!active) {
      setBanner(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const data = await fetchImpersonationBanner();
      if (!cancelled) setBanner(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  if (!active) return null;

  const onStop = async (): Promise<void> => {
    setStopping(true);
    try {
      await stopImpersonation();
    } finally {
      clearSession();
      navigate('/auth/login', { replace: true });
    }
  };

  const targetEmail = banner?.target_email ?? '';
  return (
    <div
      role="status"
      data-testid="impersonation-banner"
      className="flex h-full w-full items-center gap-3 bg-destructive px-4 text-xs font-medium text-destructive-foreground"
    >
      <span className="rounded-sm bg-destructive-foreground/15 px-1.5 py-0.5 uppercase tracking-wide">
        {t('impersonation.tag')}
      </span>
      <span className="truncate">
        {targetEmail
          ? t('impersonation.acting_as', { email: targetEmail })
          : t('impersonation.acting_as_unknown')}
      </span>
      <button
        type="button"
        onClick={() => void onStop()}
        disabled={stopping}
        data-testid="impersonation-stop"
        className="ms-auto inline-flex h-7 items-center justify-center rounded-md border border-destructive-foreground/40 bg-destructive-foreground/10 px-2.5 text-xs font-semibold hover:bg-destructive-foreground/20 disabled:opacity-60"
      >
        {stopping ? t('impersonation.stopping') : t('impersonation.stop')}
      </button>
    </div>
  );
}
